import type { ConsoleState, ActiveTool } from './types'
import type { ConsoleAction } from './actions'

export const initialConsoleState: ConsoleState = {
  assistantStatus: 'disconnected',
  mode: 'assistant',
  inputMode: 'voice',
  isConnected: false,
  connectionMode: null,
  ablyConnectionState: 'initialized',
  activeTools: [],
  messages: [],
  readouts: {},
  session: null,
  error: null,
  isMicMuted: false,
  agentMode: 'default' as const,
  controls: null,
}

type MatchPreference = 'newest' | 'oldest'

function runningToolIndices(
  activeTools: ActiveTool[],
  predicate: (entry: ActiveTool) => boolean
): number[] {
  const indices: number[] = []
  for (let i = 0; i < activeTools.length; i += 1) {
    const candidate = activeTools[i]
    if (candidate.status === 'running' && predicate(candidate)) {
      indices.push(i)
    }
  }
  return indices
}

function pickMatch(indices: number[], preference: MatchPreference): number {
  if (indices.length === 0) return -1
  return preference === 'newest' ? indices[indices.length - 1] : indices[0]
}

function findMatchingToolIndex(
  activeTools: ActiveTool[],
  {
    id,
    tool,
    invocation_id,
    operation,
    session_id,
    preference = 'newest',
  }: {
    id: string
    tool?: string
    invocation_id?: string
    operation?: string
    session_id?: string
    preference?: MatchPreference
  }
): number {
  // 1. Exact Ably message ID match (fastest, always attempted)
  const byIdIndex = activeTools.findIndex((entry) => entry.id === id)
  if (byIdIndex !== -1) return byIdIndex

  // 2. Stable invocation ID match (preferred when backend includes it)
  if (invocation_id) {
    const byInvocationIndex = activeTools.findIndex((entry) => entry.invocation_id === invocation_id)
    if (byInvocationIndex !== -1) return byInvocationIndex
  }

  // 3. Match running entries by tool + optional session scope and operation
  const matchesScope = (entry: ActiveTool): boolean => {
    if (tool && entry.tool !== tool) return false
    if (session_id && entry.session_id && entry.session_id !== session_id) return false
    return true
  }

  if (tool && operation) {
    const byToolAndOperation = runningToolIndices(
      activeTools,
      (entry) => matchesScope(entry) && entry.operation === operation
    )
    const byToolAndOperationIndex = pickMatch(byToolAndOperation, preference)
    if (byToolAndOperationIndex !== -1) return byToolAndOperationIndex
  }

  if (tool) {
    const byTool = runningToolIndices(activeTools, matchesScope)
    const byToolIndex = pickMatch(byTool, preference)
    if (byToolIndex !== -1) return byToolIndex
  }

  if (session_id) {
    const bySession = runningToolIndices(
      activeTools,
      (entry) => entry.session_id === session_id
    )
    if (bySession.length === 1) return bySession[0]
  }

  // 4. Last-resort: if exactly one running entry exists, match it regardless of
  //    tool name. Guards against n8n payloads where the `tool` field is missing or
  //    empty in completed/failed events but correct in the started event.
  const runningIndices = runningToolIndices(activeTools, () => true)
  if (runningIndices.length === 1) return runningIndices[0]

  return -1
}

export function consoleReducer(state: ConsoleState, action: ConsoleAction): ConsoleState {
  switch (action.type) {
    case 'RESET_CONSOLE':
      return initialConsoleState

    case 'ELEVENLABS_CONNECTED':
      return { ...state, isConnected: true, assistantStatus: 'idle', error: null }

    case 'ELEVENLABS_DISCONNECTED':
      return { ...state, isConnected: false, assistantStatus: 'disconnected' }

    case 'STATUS_CHANGED':
      return { ...state, assistantStatus: action.status }

    case 'MODE_CHANGED':
      return { ...state, mode: action.mode }

    case 'INPUT_MODE_CHANGED':
      return { ...state, inputMode: action.inputMode }

    case 'SESSION_STARTED':
      return { ...state, session: action.session, messages: [], activeTools: [], readouts: {} }

    case 'SESSION_ENDED': {
      // Mark any still-running tools as failed so ToolCards fade out cleanly
      // rather than staying stuck as "running" after the call drops.
      const endedAt = Date.now()
      return {
        ...state,
        session: null,
        activeTools: state.activeTools.map((t) =>
          t.status === 'running'
            ? { ...t, status: 'failed' as const, error: 'Session ended', completedAt: endedAt }
            : t
        ),
      }
    }

    case 'MESSAGE_ADDED':
      return { ...state, messages: [...state.messages, action.message] }

    case 'MESSAGE_FEEDBACK':
      return {
        ...state,
        messages: state.messages.map(m =>
          m.id === action.messageId ? { ...m, feedback: action.feedback } : m
        ),
      }

    case 'TOOL_STARTED': {
      const newTool: ActiveTool = {
        id: action.id,
        tool: action.tool,
        invocation_id: action.invocation_id,
        operation: action.operation,
        session_id: action.session_id,
        step: action.step,
        of_steps: action.of_steps,
        step_label: action.step_label,
        status: 'running',
        startedAt: action.timestamp,
      }
      // Remove any prior entry with same message id or invocation id (idempotent), then append.
      return {
        ...state,
        assistantStatus: 'tool_use',
        activeTools: [
          ...state.activeTools.filter((t) =>
            t.id !== action.id && (!action.invocation_id || t.invocation_id !== action.invocation_id)
          ),
          newTool,
        ],
      }
    }

    case 'TOOL_PROGRESS': {
      const targetIndex = findMatchingToolIndex(state.activeTools, {
        id: action.id,
        tool: action.tool,
        invocation_id: action.invocation_id,
        operation: action.operation,
        session_id: action.session_id,
        preference: 'newest',
      })
      if (targetIndex === -1) {
        // Graceful fallback for out-of-order streams: create a running entry.
        const fallbackTool: ActiveTool = {
          id: action.id,
          tool: action.tool,
          invocation_id: action.invocation_id,
          operation: action.operation,
          session_id: action.session_id,
          step: action.step,
          of_steps: action.of_steps,
          step_label: action.step_label,
          status: 'running',
          startedAt: Date.now(),
        }
        return {
          ...state,
          assistantStatus: 'tool_use',
          activeTools: [...state.activeTools, fallbackTool],
        }
      }

      return {
        ...state,
        activeTools: state.activeTools.map((tool, index) =>
          index === targetIndex
            ? {
                ...tool,
                invocation_id: action.invocation_id ?? tool.invocation_id,
                operation: action.operation ?? tool.operation,
                session_id: action.session_id ?? tool.session_id,
                step: action.step,
                of_steps: action.of_steps,
                step_label: action.step_label ?? tool.step_label,
              }
            : tool
        ),
      }
    }

    case 'TOOL_COMPLETED': {
      // Completion defaults to FIFO ordering when correlation keys are absent.
      const targetIndex = findMatchingToolIndex(state.activeTools, {
        id: action.id,
        tool: action.tool,
        invocation_id: action.invocation_id,
        operation: action.operation,
        session_id: action.session_id,
        preference: 'oldest',
      })
      const nextTools =
        targetIndex === -1
          ? [
              ...state.activeTools,
              {
                id: action.id,
                tool: action.tool,
                invocation_id: action.invocation_id,
                operation: action.operation,
                session_id: action.session_id,
                step: action.step ?? 1,
                of_steps: action.of_steps ?? null,
                status: 'completed' as const,
                startedAt: action.timestamp,
                completedAt: action.timestamp,
              },
            ]
          : state.activeTools.map((tool, index) =>
              index === targetIndex
                ? {
                    ...tool,
                    invocation_id: action.invocation_id ?? tool.invocation_id,
                    operation: action.operation ?? tool.operation,
                    session_id: action.session_id ?? tool.session_id,
                    step: action.step ?? tool.step,
                    of_steps: action.of_steps ?? tool.of_steps,
                    status: 'completed' as const,
                    completedAt: action.timestamp,
                  }
                : tool
            )
      const stillRunning = nextTools.some((tool) => tool.status === 'running')
      return {
        ...state,
        assistantStatus: stillRunning ? 'tool_use' : 'processing',
        activeTools: nextTools,
      }
    }

    case 'TOOL_FAILED': {
      // Failure defaults to FIFO ordering when correlation keys are absent.
      const targetIndex = findMatchingToolIndex(state.activeTools, {
        id: action.id,
        tool: action.tool,
        invocation_id: action.invocation_id,
        operation: action.operation,
        session_id: action.session_id,
        preference: 'oldest',
      })
      const nextTools =
        targetIndex === -1
          ? [
              ...state.activeTools,
              {
                id: action.id,
                tool: action.tool,
                invocation_id: action.invocation_id,
                operation: action.operation,
                session_id: action.session_id,
                step: 1,
                of_steps: null,
                status: 'failed' as const,
                error: action.error,
                startedAt: action.timestamp,
                completedAt: action.timestamp,
              },
            ]
          : state.activeTools.map((tool, index) =>
              index === targetIndex
                ? {
                    ...tool,
                    invocation_id: action.invocation_id ?? tool.invocation_id,
                    operation: action.operation ?? tool.operation,
                    session_id: action.session_id ?? tool.session_id,
                    status: 'failed' as const,
                    error: action.error,
                    completedAt: action.timestamp,
                  }
                : tool
            )
      const stillRunning = nextTools.some((tool) => tool.status === 'running')
      return {
        ...state,
        assistantStatus: stillRunning ? 'tool_use' : 'idle',
        activeTools: nextTools,
      }
    }

    case 'TOOL_DISMISSED':
      return { ...state, activeTools: state.activeTools.filter((t) => t.id !== action.id) }

    case 'READOUT_UPDATED':
      return {
        ...state,
        readouts: { ...state.readouts, [action.key]: action.value },
      }

    case 'ABLY_CONNECTION_STATE_CHANGED':
      return { ...state, ablyConnectionState: action.connectionState }

    case 'ERROR_SET':
      return { ...state, error: action.error, assistantStatus: 'error' }

    case 'ERROR_CLEARED':
      return { ...state, error: null }

    case 'SET_CONTROLS':
      return { ...state, controls: action.controls }

    case 'SET_CONNECTION_MODE':
      return { ...state, connectionMode: action.mode }

    case 'SET_MIC_MUTED':
      return { ...state, isMicMuted: action.muted }

    case 'SET_AGENT_MODE':
      return { ...state, agentMode: action.agentMode }

    default:
      return state
  }
}
